import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { X, Plus, Menu, Trash2, ArrowUp, Square, Copy, Check, Info } from 'lucide-react';
import { getConversations, getConversation, deleteConversation, getUsage } from '../../services/aiChatService';
import LeaveConfirmCard from './LeaveConfirmCard';

/* ─── animations ─── */
const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const historySlideIn = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

/* ─── styled components ─── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
`;

const Panel = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 480px;
  max-width: 100vw;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 66, 66, 0.12);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  animation: ${({ $closing }) => ($closing ? slideOut : slideIn)} 0.3s ease forwards;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #E8ECF0;
  gap: 10px;
  flex-shrink: 0;
`;

const AiAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0291EF, #02D3BF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  flex-shrink: 0;
`;

// Başlık + konuşma sayacı tek bir dikey blok. `flex: 1` HeaderTitle'dan buraya
// taşındı; aksi halde sayaç satırı başlığın yanına düşüp sağdaki butonları sıkıştırırdı.
const HeaderTitleBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderTitle = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.2;
`;

// Konuşma kotası (kullanıcı başına 10) — alttaki satırdaki GÜNLÜK MESAJ kotasından
// AYRI bir sınırdır. Sınıra gelindiğinde "+" butonu pasifleşiyordu ama sayı hiçbir
// yerde yazmıyordu; kullanıcı neden yeni konuşma açamadığını ancak ipucunu görürse
// anlıyordu.
const ConversationUsage = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  color: ${({ $limitReached, theme }) => ($limitReached ? '#E53935' : theme.colors.text.dark.little)};
  line-height: 1.2;
`;

const HeaderBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const BodyWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
`;

const HistorySidebar = styled.div`
  width: 240px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #E8ECF0;
  overflow-y: auto;
  animation: ${historySlideIn} 0.2s ease;
  display: flex;
  flex-direction: column;
`;

const HistoryTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 12px 14px;
  border-bottom: 1px solid #E8ECF0;
`;

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 8px;
  cursor: pointer;
  border-bottom: 1px solid #F0F2F5;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.06)' : 'transparent')};
  transition: background 0.15s;

  &:hover {
    background: rgba(2, 145, 239, 0.06);
  }
`;

const HistoryItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HistoryItemTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HistoryItemDate = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const HistoryDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;

  ${HistoryItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.auxiliar.danger};
  }
`;

const EmptyHistory = styled.div`
  padding: 24px 14px;
  text-align: center;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WelcomeMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  padding: 32px 24px;
  gap: 16px;
`;

const WelcomeAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0291EF, #02D3BF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const WelcomeText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  line-height: 1.5;
  max-width: 320px;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

const MessageBubble = styled.div`
  max-width: 85%;
  padding: 10px 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.55;
  position: relative;
  word-break: break-word;

  ${({ $role }) =>
    $role === 'user'
      ? `
    background: linear-gradient(120deg, #0291EF, #02D3BF);
    color: #fff;
    border-radius: 12px 12px 0 12px;
  `
      : `
    background: #F5F7FA;
    color: #004242;
    border-radius: 12px 12px 12px 0;
  `}
`;

const CopyBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s;

  ${MessageBubble}:hover & {
    opacity: 1;
  }
`;

const StreamingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 10px 16px;
  background: #F5F7FA;
  border-radius: 12px 12px 12px 0;
  max-width: 60px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.text.dark.little};
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.2}s;
`;

const FooterArea = styled.div`
  border-top: 1px solid #E8ECF0;
  padding: 10px 16px 14px;
  flex-shrink: 0;
`;

const UsageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  min-height: 20px;
`;

const UsageText = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ $color }) => $color || '#667'};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
`;

const InfoIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover > div {
    opacity: 1;
    pointer-events: auto;
  }
`;

const InfoTooltip = styled.div`
  position: absolute;
  bottom: 24px;
  right: 0;
  width: 220px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  padding: 8px 10px;
  border-radius: 6px;
  line-height: 1.4;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  z-index: 10;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const TextArea = styled.textarea`
  flex: 1;
  border: 1px solid #E8ECF0;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: none;
  min-height: 40px;
  max-height: 120px;
  outline: none;
  line-height: 1.4;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary.main};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.dark.little};
  }
`;

const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ disabled }) =>
    disabled ? '#E8ECF0' : 'linear-gradient(120deg, #0291EF, #02D3BF)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: opacity 0.15s;
  flex-shrink: 0;
`;

/* ─── markdown parser ─── */
function parseMarkdown(text, history) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeLines = [];
  let key = 0;

  const parseInline = (line) => {
    const parts = [];
    // combined regex: bold, inline code, links
    const regex = /(\*\*(.+?)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[1]) {
        // bold
        parts.push(<strong key={`b-${match.index}`}>{match[2]}</strong>);
      } else if (match[3]) {
        // inline code
        parts.push(
          <code key={`c-${match.index}`} style={{
            background: '#F0F2F5',
            padding: '2px 6px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: '13px',
          }}>{match[4]}</code>
        );
      } else if (match[5]) {
        // link
        const label = match[6];
        const href = match[7];
        if (href.startsWith('/')) {
          parts.push(
            <a
              key={`l-${match.index}`}
              href={href}
              style={{ color: '#0291EF', textDecoration: 'none', cursor: 'pointer' }}
              onClick={(e) => { e.preventDefault(); history.push(href); }}
            >{label}</a>
          );
        } else {
          parts.push(
            <a
              key={`l-${match.index}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0291EF', textDecoration: 'none' }}
            >{label}</a>
          );
        }
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    return parts.length ? parts : [line];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // code block toggle
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={key++} style={{
            background: '#1e293b',
            color: '#e2e8f0',
            padding: '12px',
            borderRadius: '6px',
            fontFamily: 'monospace',
            fontSize: '13px',
            overflowX: 'auto',
            margin: '6px 0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    // headers
    if (line.startsWith('### ')) {
      elements.push(
        <div key={key++} style={{ fontWeight: 700, fontSize: '14px', margin: '8px 0 4px' }}>
          {parseInline(line.slice(4))}
        </div>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <div key={key++} style={{ fontWeight: 700, fontSize: '15px', margin: '8px 0 4px' }}>
          {parseInline(line.slice(3))}
        </div>
      );
      continue;
    }

    // unordered list
    if (/^[-*]\s/.test(line.trim())) {
      elements.push(
        <div key={key++} style={{ paddingLeft: '16px', margin: '2px 0' }}>
          {'• '}{parseInline(line.trim().slice(2))}
        </div>
      );
      continue;
    }

    // ordered list
    if (/^\d+\.\s/.test(line.trim())) {
      const num = line.trim().match(/^(\d+)\.\s/)[1];
      elements.push(
        <div key={key++} style={{ paddingLeft: '16px', margin: '2px 0' }}>
          {num}. {parseInline(line.trim().replace(/^\d+\.\s/, ''))}
        </div>
      );
      continue;
    }

    // empty line
    if (!line.trim()) {
      elements.push(<div key={key++} style={{ height: '6px' }} />);
      continue;
    }

    // normal paragraph
    elements.push(<div key={key++}>{parseInline(line)}</div>);
  }

  // unclosed code block
  if (inCodeBlock && codeLines.length) {
    elements.push(
      <pre key={key++} style={{
        background: '#1e293b',
        color: '#e2e8f0',
        padding: '12px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '13px',
        overflowX: 'auto',
        margin: '6px 0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        <code>{codeLines.join('\n')}</code>
      </pre>
    );
  }

  return elements;
}

/* ─── relative date helper ─── */
function relativeDate(dateStr, locale = 'tr') {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'az once';
  if (diffMin < 60) return `${diffMin}dk`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}sa`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}g`;
  return d.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/* ─── main component ─── */
export default function AiChatModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [usage, setUsage] = useState({
    messagesUsedToday: 0, dailyLimit: 10, hasAiPlusModule: false,
    conversationCount: 0, conversationLimit: 10,
  });
  const [showHistory, setShowHistory] = useState(false);
  const [inputText, setInputText] = useState('');
  const [closing, setClosing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const remaining = usage.dailyLimit - usage.messagesUsedToday;
  const conversationLimitReached =
    (usage.conversationCount ?? 0) >= (usage.conversationLimit ?? 10);

  /* ─── fetch usage + conversations on open ─── */
  useEffect(() => {
    if (!open) return;
    setClosing(false);
    getUsage()
      .then((res) => {
        const u = res.data?.data ?? res.data;
        if (u && typeof u.dailyLimit === 'number' && typeof u.messagesUsedToday === 'number') {
          setUsage(u);
        }
      })
      .catch(() => {});
    getConversations()
      .then((res) => { const d = res.data?.data ?? res.data; setConversations(Array.isArray(d) ? d : []); })
      .catch(() => {});
  }, [open]);

  /* ─── auto-scroll ─── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  /* ─── auto-resize textarea ─── */
  const handleTextareaInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '40px';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  /* ─── load conversation ─── */
  const loadConversation = useCallback(async (id) => {
    try {
      const res = await getConversation(id);
      const detail = res.data?.data ?? res.data;
      setActiveConversationId(id);
      setMessages(detail?.messages || []);
      setShowHistory(false);
    } catch {
      // error handled by interceptor
    }
  }, []);

  /* ─── new conversation ─── */
  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setStreamingText('');
    setInputText('');
  }, []);

  /* ─── delete conversation ─── */
  const handleDeleteConversation = useCallback(async (e, id) => {
    e.stopPropagation();
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      setUsage((prev) => ({
        ...prev,
        conversationCount: Math.max(0, (prev.conversationCount ?? 0) - 1),
      }));
      if (activeConversationId === id) {
        startNewConversation();
      }
    } catch {
      // error handled by interceptor
    }
  }, [activeConversationId, startNewConversation]);

  /* ─── copy message ─── */
  const handleCopy = useCallback((text, msgIndex) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(msgIndex);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  /* ─── send message / SSE streaming ─── */
  const handleSend = useCallback(async () => {
    const message = inputText.trim();
    if (!message || isStreaming || remaining <= 0) return;

    const userMsg = { role: 'user', content: message };
    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    setStreamingText('');
    setIsStreaming(true);

    // reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    let currentConvId = activeConversationId;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5148'}/v1/ai-chat/chat`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            conversationId: currentConvId,
            message,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }
      );

      if (!response.ok) {
        const mesaj = response.status === 429
          ? t('aiChat.rateLimited')
          : t('aiChat.errorOccurred');
        setMessages((prev) => [...prev, { role: 'assistant', content: mesaj }]);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.content || '';

            if (content.startsWith('[CONVERSATION_ID]')) {
              const newId = content.replace('[CONVERSATION_ID]', '');
              currentConvId = newId;
              setActiveConversationId(newId);
            } else if (content.startsWith('[CONFIRM]')) {
              // Onay kartı yükü. Kart yalnız canlı oturumda yaşar — sunucuda
              // saklanmaz, geçmiş yeniden yüklendiğinde tekrar gösterilmez.
              try {
                const card = JSON.parse(content.replace('[CONFIRM]', ''));
                setMessages((prev) => [...prev, { role: 'assistant', content: '', card }]);
              } catch {
                /* bozuk yük yok sayılır */
              }
            } else if (content.startsWith('[ERROR]')) {
              const errorKey = content.replace('[ERROR]', '');
              const errorMsg = t(`responseErrors.${errorKey}`, { defaultValue: t('aiChat.errorOccurred') });
              setMessages((prev) => [...prev, { role: 'assistant', content: errorMsg }]);
            } else {
              accumulated += content;
              setStreamingText(accumulated);
            }
          } catch {
            /* skip non-JSON lines */
          }
        }
      }

      // finalize streaming
      if (accumulated) {
        setMessages((prev) => [...prev, { role: 'assistant', content: accumulated }]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => [...prev, { role: 'assistant', content: t('aiChat.errorOccurred') }]);
      }
    } finally {
      setStreamingText('');
      setIsStreaming(false);
      abortControllerRef.current = null;

      // refresh usage
      getUsage()
        .then((res) => {
          const u = res.data?.data ?? res.data;
          if (u && typeof u.dailyLimit === 'number' && typeof u.messagesUsedToday === 'number') {
            setUsage(u);
          }
        })
        .catch(() => {});

      // refresh conversations list
      getConversations()
        .then((res) => { const d = res.data?.data ?? res.data; setConversations(Array.isArray(d) ? d : []); })
        .catch(() => {});
    }
  }, [inputText, isStreaming, remaining, activeConversationId, t]);

  /* ─── abort streaming ─── */
  const handleAbort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /* ─── key handler ─── */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  /* ─── close with animation ─── */
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  if (!open && !closing) return null;

  const usageColor = remaining <= 0 ? '#E53935' : remaining <= 3 ? '#FFC107' : '#667';

  return (
    <>
      <Overlay onClick={handleClose} />
      <Panel $closing={closing}>
        {/* header */}
        <HeaderBar>
          <AiAvatar>AI</AiAvatar>
          <HeaderTitleBlock>
            <HeaderTitle>{t('aiChat.title')}</HeaderTitle>
            <ConversationUsage $limitReached={conversationLimitReached}>
              {t('aiChat.conversationUsage', {
                used: usage.conversationCount ?? 0,
                limit: usage.conversationLimit ?? 10,
              })}
            </ConversationUsage>
          </HeaderTitleBlock>
          <HeaderBtn
            onClick={startNewConversation}
            disabled={conversationLimitReached}
            title={conversationLimitReached
              ? t('aiChat.conversationLimitHint')
              : t('aiChat.newConversation')}
          >
            <Plus size={18} />
          </HeaderBtn>
          <HeaderBtn onClick={() => setShowHistory((v) => !v)} title={t('aiChat.history')}>
            <Menu size={18} />
          </HeaderBtn>
          <HeaderBtn onClick={handleClose}>
            <X size={18} />
          </HeaderBtn>
        </HeaderBar>

        <BodyWrapper>
          {/* history sidebar */}
          {showHistory && (
            <HistorySidebar>
              <HistoryTitle>{t('aiChat.history')}</HistoryTitle>
              <HistoryList>
                {conversations.length === 0 ? (
                  <EmptyHistory>{t('aiChat.noConversations')}</EmptyHistory>
                ) : (
                  conversations.map((conv) => (
                    <HistoryItem
                      key={conv.id}
                      $active={conv.id === activeConversationId}
                      onClick={() => loadConversation(conv.id)}
                    >
                      <HistoryItemText>
                        <HistoryItemTitle>
                          {conv.title || t('aiChat.untitled')}
                        </HistoryItemTitle>
                        <HistoryItemDate>{relativeDate(conv.updatedAt || conv.createdAt, i18n.language)}</HistoryItemDate>
                      </HistoryItemText>
                      <HistoryDeleteBtn onClick={(e) => handleDeleteConversation(e, conv.id)}>
                        <Trash2 size={14} />
                      </HistoryDeleteBtn>
                    </HistoryItem>
                  ))
                )}
              </HistoryList>
            </HistorySidebar>
          )}

          {/* messages */}
          <MessagesArea>
            {messages.length === 0 && !streamingText ? (
              <WelcomeMessage>
                <WelcomeAvatar>AI</WelcomeAvatar>
                <WelcomeText>{t('aiChat.welcome')}</WelcomeText>
              </WelcomeMessage>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <MessageRow key={idx} $role={msg.role}>
                    {msg.card ? (
                      <LeaveConfirmCard
                        payload={msg.card}
                        onDone={() => {
                          // İşlem sonrası kota göstergesini tazele
                          getUsage()
                            .then((res) => {
                              const u = res.data?.data ?? res.data;
                              if (u && typeof u.dailyLimit === 'number') setUsage(u);
                            })
                            .catch(() => {});
                        }}
                      />
                    ) : (
                      <MessageBubble $role={msg.role}>
                        {msg.role === 'assistant' ? parseMarkdown(msg.content, history) : msg.content}
                        <CopyBtn onClick={() => handleCopy(msg.content, idx)}>
                          {copiedId === idx ? (
                            <Check size={14} color="#02D3BF" />
                          ) : (
                            <Copy size={14} color="#667" />
                          )}
                        </CopyBtn>
                      </MessageBubble>
                    )}
                  </MessageRow>
                ))}
                {streamingText && (
                  <MessageRow $role="assistant">
                    <MessageBubble $role="assistant">
                      {parseMarkdown(streamingText, history)}
                    </MessageBubble>
                  </MessageRow>
                )}
                {isStreaming && !streamingText && (
                  <MessageRow $role="assistant">
                    <StreamingDots>
                      <Dot $i={0} />
                      <Dot $i={1} />
                      <Dot $i={2} />
                    </StreamingDots>
                  </MessageRow>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>
        </BodyWrapper>

        {/* footer */}
        <FooterArea>
          <UsageRow>
            <UsageText $color={usageColor}>
              {remaining <= 0
                ? t('aiChat.limitReached')
                : t('aiChat.remaining', { count: remaining, limit: usage.dailyLimit })}
            </UsageText>
            {!usage.hasAiPlusModule && (
              <InfoIconWrapper>
                <Info size={16} color="#667" />
                <InfoTooltip>{t('aiChat.usageTooltip')}</InfoTooltip>
              </InfoIconWrapper>
            )}
          </UsageRow>
          <InputRow>
            <TextArea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                handleTextareaInput();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('aiChat.placeholder')}
              rows={1}
              disabled={remaining <= 0}
            />
            {isStreaming ? (
              <SendBtn onClick={handleAbort} type="button">
                <Square size={16} color="#fff" fill="#fff" />
              </SendBtn>
            ) : (
              <SendBtn
                onClick={handleSend}
                disabled={!inputText.trim() || remaining <= 0}
                type="button"
              >
                <ArrowUp size={18} color="#fff" />
              </SendBtn>
            )}
          </InputRow>
        </FooterArea>
      </Panel>
    </>
  );
}
